import { useLocalstorage, useRecordLastPage } from "@src/hooks/helperHooks";
import {
  useOrganization,
  useOrganizations,
} from "@src/modules/member/hooks/organizations";
import { Organization } from "@src/modules/member/models/organization";
import { Dispatch, createContext, useEffect, useMemo } from "react";

import { LAST_APP_PAGE_VISITED_KEY } from "../../constants";
import { useAuthorized } from "../../hooks/authorized";
import { memberSettingsPathPrefix } from "../../path";

export const OrganizationContext = createContext<{
  currentOrganization: Organization | undefined;
  currentOrganizationCode: string | null;
  setCurrentOrganizationCode: Dispatch<string | null>;
  isLoading: boolean;
}>({
  currentOrganization: undefined,
  currentOrganizationCode: null,
  setCurrentOrganizationCode: () => {},
  isLoading: false,
});

export default function OrganizationContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  useAuthorized();
  useRecordLastPage(LAST_APP_PAGE_VISITED_KEY, [memberSettingsPathPrefix]);
  const {
    data: organizationsResponse,
    isLoading,
    isFetching,
  } = useOrganizations();
  const organizations = organizationsResponse?.data;

  const firstOrganizationCode = organizations?.[0]?.code ?? null;
  const [storedOrganizationCode, setStoredOrganizationCode] = useLocalstorage(
    "CURRENT_ORGANIZATION_CODE",
    null,
  );

  const currentOrganizationCode = useMemo(() => {
    if (!organizations || organizations.length === 0) {
      return null;
    }

    if (
      storedOrganizationCode &&
      organizations.some((org) => org.code === storedOrganizationCode)
    ) {
      return storedOrganizationCode;
    }

    return firstOrganizationCode;
  }, [organizations, storedOrganizationCode, firstOrganizationCode]);

  const { data: currentOrganizationRes } = useOrganization(
    currentOrganizationCode ?? "",
  );
  const currentOrganization = currentOrganizationRes?.data;

  useEffect(() => {
    if (
      currentOrganizationCode &&
      currentOrganizationCode !== storedOrganizationCode &&
      !isFetching
    ) {
      setStoredOrganizationCode(currentOrganizationCode);
    }
  }, [
    currentOrganizationCode,
    storedOrganizationCode,
    setStoredOrganizationCode,
    isFetching,
  ]);

  const contextValue = useMemo(
    () => ({
      currentOrganization,
      currentOrganizationCode,
      setCurrentOrganizationCode: setStoredOrganizationCode,
      isLoading,
    }),
    [
      currentOrganization,
      currentOrganizationCode,
      setStoredOrganizationCode,
      isLoading,
    ],
  );

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-primary-500" />
      </div>
    );
  }

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}
